<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">

    <!-- Top Action Bar -->
    <div class="w-full flex justify-between items-center mb-6">
        <app-btn-back routeTo=".."></app-btn-back>
    </div>

    <!-- Redesigned Flexbox-Based Layout -->
    <div class="flex flex-col lg:flex-row gap-6">

        <!-- Left Column: Member Card & Profile Details (flex-4) -->
        <div class="w-full lg:flex-[4]" style="flex: 4 0 0%;">
            <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center">

                <!-- Circular Avatar Container -->
                <div class="relative">
                    @if (member?.photoURL) {
                    <img [src]="member?.photoURL"
                        class="h-40 w-40 bg-white object-cover rounded-full shadow-md border border-gray-100" />
                    } @else {
                    <div
                        class="bg-primary-100 uppercase text-primary-500 text-5xl flex justify-center items-center text-center h-40 w-40 rounded-full shadow-md border border-gray-100 font-semibold">
                        {{ member?.name! | initials}}
                    </div>
                    }
                </div>

                <!-- Member Name -->
                <h3 class="text-2xl font-bold text-gray-900 mt-5 text-center">
                    {{ member?.name }}
                </h3>

                <!-- Divider -->
                <div class="w-full border-t border-gray-100 my-6"></div>

                <!-- Info List: Reusable spacing & typography -->
                <div class="w-full space-y-5">
                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">First name</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1">{{ firstName || '—' }}</span>
                    </div>

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Last name</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1">{{ lastName || '—' }}</span>
                    </div>

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Mobile
                            number</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1">
                            @if (member?.contact?.contact) {
                            {{ member?.contact?.phoneCode ? '+' + member?.contact?.phoneCode : '' }} {{
                            member?.contact?.contact }}
                            } @else {
                            —
                            }
                        </span>
                    </div>

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Email
                            address</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1 break-all">{{ member?.email || '—'
                            }}</span>
                    </div>

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Date of
                            birth</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1">
                            @if (member?.dob) {
                            {{ member!.dob! | moment: 'D MMM YYYY' }}
                            } @else {
                            —
                            }
                        </span>
                    </div>

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Gender</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1 capitalize">{{ member?.gender ||
                            '—' }}</span>
                    </div>

                    @if (member?.profession) {
                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Profession</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1 capitalize">{{ member?.profession
                            }}</span>
                    </div>
                    }
                </div>
            </div>
        </div>

        <!-- Right Column: Addresses & Maps & Status Widget (flex-8) -->
        <div class="w-full lg:flex-[8] flex flex-col gap-6" style="flex: 8 0 0%;">

            <!-- Addresses Card -->
            <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 class="text-lg font-bold text-gray-900 mb-5">
                    My addresses
                </h3>

                @if (member?.address && (member?.address?.addressLine1 || member?.address?.city)) {
                <!-- Clean address card matching design -->
                <div class="bg-gray-50 rounded-xl p-5 border border-gray-100 relative mb-6">
                    <div class="flex items-start justify-between">
                        <div>
                            <span class="block font-semibold text-gray-800 text-sm">Home</span>
                            <span class="block text-sm text-gray-600 mt-2">
                                {{ member?.address?.buildingNo ? member?.address?.buildingNo + ' ' : '' }}
                                {{ member?.address?.buildingName ? member?.address?.buildingName + ', ' : '' }}
                                {{ member?.address?.addressLine1 }}
                                {{ member?.address?.addressLine2 ? ', ' + member?.address?.addressLine2 : '' }}
                            </span>
                            <span class="block text-sm text-gray-500 mt-1">
                                {{ member?.address?.city }}{{ member?.address?.country ? ', ' + member?.address?.country
                                : '' }}
                            </span>
                        </div>
                    </div>
                </div>
                } @else {
                <div class="text-sm text-gray-400 py-6 italic">
                    No address information provided.
                </div>
                }

                <!-- Map (shown only if location is provided by member) -->
                @if (member?.location?.position) {
                <div class="border-t border-gray-100 pt-6">
                    <span class="block uppercase text-gray-400 text-xs font-bold mb-4 tracking-wider">
                        Location Map
                    </span>
                    <div class="rounded-xl overflow-hidden shadow-inner border border-gray-100">
                        <app-view-location [location]="member?.location"></app-view-location>
                    </div>
                </div>
                }
            </div>

            <!-- Administrative Membership Status Widget -->
            <div
                class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h4 class="font-bold text-gray-900">Membership Status</h4>
                    <p class="text-xs text-gray-400 mt-1">Status controls for group membership</p>
                </div>
                <div>
                    <app-btn-member-status [status]="member?.status"
                        [isDisabled]="(member?.memberType ?? []).indexOf(memberType.admin) == -1 && member?.status != membershipStatus.active"
                        (click)="changeStatusDialog(member?.status)"></app-btn-member-status>
                </div>
            </div>

        </div>
    </div>
</div>